import { API_BASE_URL } from "@/config";
import { validate_form } from "@/utils/signio_validation.js";


export default {
  name: "IncomeInputGrid",
  emits: ["validity-change", "previous"],
  data() {
    const config = [
      {
        boolean_key: "sourceOfFundsBonus",
        boolean_label: "Is the source of funds a bonus?",
        amount_key: "sourceOfFundsBonusAmount",
        short_label: "Bonus",
        category: "amount",
      },
      {
        boolean_key: "sourceOfFundsCommissionsEarned",
        boolean_label: "Is the source of funds commissions earned?",
        amount_key: "sourceOfFundsCommissionsEarnedAmount",
        short_label: "Commissions",
        category: "amount",
      },
      {
        boolean_key: "sourceOfFundsCourtOrder",
        boolean_label: "Is the source of funds a court order?",
        amount_key: "sourceOfFundsCourtOrderAmount",
        short_label: "Court Order",
        category: "amount",
      },
      {
        boolean_key: "sourceOfFundsDividends",
        boolean_label: "Is the source of funds dividends?",
        amount_key: "sourceOfFundsDividendsAmount",
        short_label: "Dividends",
        category: "amount",
      },
      {
        boolean_key: "sourceOfFundsDonations",
        boolean_label: "Is the source of funds donations?",
        amount_key: "sourceOfFundsDonationsAmount",
        short_label: "Donations",
        category: "amount",
      },
      {
        boolean_key: "sourceOfFundsInheritance",
        boolean_label: "Is the source of funds inheritance?",
        amount_key: "sourceOfFundsInheritanceAmount",
        short_label: "Inheritance",
        category: "amount",
      },
      {
        boolean_key: "sourceOfFundsInsuranceClaim",
        boolean_label: "Is the source of funds an insurance claim?",
        amount_key: "sourceOfFundsInsuranceClaimAmount",
        short_label: "Insurance Claim",
        category: "amount",
      },
      {
        boolean_key: "sourceOfFundsInterest",
        boolean_label: "Is the source of funds interest?",
        amount_key: "sourceOfFundsInterestAmount",
        short_label: "Interest",
        category: "amount",
      },
      {
        boolean_key: "sourceOfFundsInvestmentIncome",
        boolean_label: "Is the source of funds investment income?",
        amount_key: "sourceOfFundsInvestmentIncomeAmount",
        short_label: "Investment Income",
        category: "amount",
      },
      {
        boolean_key: "sourceOfFundsMaintenance",
        boolean_label: "Is the source of funds maintenance?",
        amount_key: "sourceOfFundsMaintenanceAmount",
        short_label: "Maintenance",
        category: "amount",
      },
      {
        boolean_key: "sourceOfFundsPension",
        boolean_label: "Is the source of funds pension?",
        amount_key: "sourceOfFundsPensionAmount",
        short_label: "Pension",
        category: "amount",
      },
      {
        boolean_key: "sourceOfFundsAllowance",
        boolean_label: "Is the source of funds an allowance?",
        amount_key: null,
      },
      {
        boolean_key: "sourceOfFundsProceedsFromOwnBusiness",
        boolean_label: "Is the source of funds proceeds from own business?",
        amount_key: null,
      },
      {
        boolean_key: "sourceOfFundsRentals",
        boolean_label: "Is the source of funds rentals?",
        amount_key: null,
      },
      {
        boolean_key: "sourceOfFundsRetirementAnnuity",
        boolean_label: "Is the source of funds a retirement annuity?",
        amount_key: "sourceOfFundsRetirementAnnuityAmount",
        short_label: "Retirement Annuity",
      },
      {
        boolean_key: "sourceOfFundsSalary",
        boolean_label: "Is the source of funds salary?",
        amount_key: null,
      },
      {
        boolean_key: "sourceOfFundsSocialBenefits",
        boolean_label: "Is the source of funds social benefits?",
        amount_key: null,
      },
      {
        boolean_key: "sourceOfFundsSocialGrant",
        boolean_label: "Is the source of funds a social grant?",
        amount_key: null,
      },
      {
        boolean_key: "sourceOfFundsWinnings",
        boolean_label: "Is the source of funds winnings?",
        amount_key: "sourceOfFundsWinningsAmount",
        short_label: "Winnings",
      },
      {
        boolean_key: "sourceOfFundsBEE",
        boolean_label: "Is the source of funds BEE income?",
        amount_key: "sourceOfFundsBEEAmount",
        short_label: "BEE Income",
      },
    ];
    const amount_fields = config.filter((f) => f.amount_key);
    const boolean_fields = config.filter((f) => !f.amount_key);
    const form_data = {};
    config.forEach((f) => {
      form_data[f.boolean_key] = null;
      if (f.amount_key) form_data[f.amount_key] = null;
    });
    return {
      amount_fields,
      boolean_fields,
      form_data,
      // Conditional question state
      has_extra_income: null, // null = not answered, true = yes, false = no
      collapsed_sections: {
        income_amounts: false,
        income_options: false,
      },
    };
  },
  computed: {
    workflow_id() {
      return this.$store.getters.get_workflow_id;
    },
    // Computed property to determine if income details should be shown
    show_income_details() {
      return this.has_extra_income === true;
    },
    // Check if the conditional question has been answered
    conditional_question_answered() {
      return this.has_extra_income !== null;
    },
  },
  watch: {
    // Watch for changes in has_extra_income to clear form data when user selects "No"
    has_extra_income(newVal) {
      if (newVal === false) {
        // Reset all income fields when user says they have no extra income
        this.reset_income_fields();
      }
    },
  },
  methods: {
    previous() {
      this.$emit("previous");
    },
    toggle(section) {
      this.collapsed_sections[section] = !this.collapsed_sections[section];
    },
    // Reset all income fields when user indicates no extra income
    reset_income_fields() {
      this.amount_fields.forEach(field => {
        this.form_data[field.boolean_key] = false;
        if (field.amount_key) {
          this.form_data[field.amount_key] = "0.00";
        }
      });
      this.boolean_fields.forEach(field => {
        this.form_data[field.boolean_key] = false;
      });
    },
    // Check if any income values have been filled in
    has_any_income_values() {
      // Check amount fields
      const has_amounts = this.amount_fields.some(field => {
        const amount = parseFloat(this.form_data[field.amount_key]);
        return !isNaN(amount) && amount > 0;
      });
      // Check boolean fields
      const has_booleans = this.boolean_fields.some(field => {
        return this.form_data[field.boolean_key] === true;
      });
      return has_amounts || has_booleans;
    },
    on_toggle(field) {
      if (!this.form_data[field.boolean_key] && field.amount_key) {
        this.form_data[field.amount_key] = null;
      }
    },
    on_amount_input(field) {
      if (!field.amount_key) return;
      this.form_data[field.boolean_key] =
        Number(this.form_data[field.amount_key]) > 0;
    },
    format_amount(field) {
      if (!field.amount_key) return;
      const key = field.amount_key;
      let val = parseFloat(this.form_data[key]);
      val = isNaN(val) ? 0 : val;
      this.form_data[key] = val.toFixed(2);
      this.form_data[field.boolean_key] = val > 0;
    },
    submit_section() {
      const token = localStorage.getItem("token");
      const service_api = `${API_BASE_URL}/verify_api/financial_information_api/sygnio_answer/`;
      
      // Validate that the conditional question has been answered
      if (this.has_extra_income === null) {
        Swal.fire({
          title: "Validation Error",
          text: "Please indicate whether you have any extra income sources.",
          icon: "error",
          timer: 3000,
          timerProgressBar: true,
          showConfirmButton: false,
        });
        return;
      }

      // If user has no extra income, set all values to defaults
      if (!this.has_extra_income) {
        this.reset_income_fields();
      }
      
      this.amount_fields.forEach(field => {
        const amountKey = field.amount_key;
        const boolKey = field.boolean_key;
        if (
          this.form_data[amountKey] === null ||
          this.form_data[amountKey] === "" ||
          isNaN(parseFloat(this.form_data[amountKey]))
        ) {
          this.form_data[amountKey] = "0.00";
          this.form_data[boolKey] = false;
        }
      });

      this.boolean_fields.forEach(field => {
        const boolKey = field.boolean_key;
        if (this.form_data[boolKey] === null || this.form_data[boolKey] === "") {
          this.form_data[boolKey] = false;
        }
      });

      const answers = Object.keys(this.form_data).map((key) => ({
        answer: this.form_data[key],
        api_form_field: key,
      }));
      
      // Note: has_extra_income is only used for UI rendering - NOT sent to backend
      
      var errors = validate_form(this.form_data);
      if (errors.length > 0) {
        {
          const msg = errors
            .map(err => {
              return `${err.error}`
            })
            .join("\n")

          Swal.fire({
            title: "Validation Error",
            text: msg,
            icon: "error",
            timer: 3000,
            timerProgressBar: true,
            showConfirmButton: false,
          })
        }
        return;
      }

      $.ajax({
        type: "POST",
        url: service_api,
        headers: { Authorization: `Token ${token}` },
        data: JSON.stringify({ answers, workflow_id: this.workflow_id }),
        contentType: "application/json",
        success: (response) => {
          if (response.status === "success") {
            Swal.fire({
              title: "Success",
              text: "Your information has been submitted successfully!",
              icon: "success",
              timer: 3000,
              timerProgressBar: true,
              showConfirmButton: false,
            }).then(() => {
              this.$emit("validity-change", true)
            });
          }
        },
        error: () => {
          Swal.fire({
            title: "Error",
            text: "Failed to submit the information, try again later.",
            icon: "error",
            timer: 3000,
            timerProgressBar: true,
            showConfirmButton: false,
          });
        },
      });
    },
    // helper to normalize Python‑string booleans and numeric amounts
    normalize(key, value) {
      if (value === "True" || value === "true") {
        return true;
      }
      if (value === "False" || value === "false") {
        return false;
      }
      // if it looks like a number and the field name mentions "amount"
      if (!isNaN(value) && /amount/i.test(key)) {
        return parseFloat(value).toFixed(2);
      }
      return value;
    },
    fetch_sygnio_answers() {
      const token = localStorage.getItem("token");
      const service_api = `${API_BASE_URL}/verify_api/financial_information_api/get_sygnio_question_answers/`;

      $.ajax({
        type: "POST",
        url: service_api,
        headers: {
          Authorization: `Token ${token}`,
        },
        data: JSON.stringify({ workflow_id: this.workflow_id }),
        contentType: "application/json",
        success: (response) => {
          if (response.status === "success" && response.data) {
            const data = response.data;

            if (Array.isArray(data)) {
              data.forEach((item) => {
                const { api_form_field: key, answer } = item;
                if (this.form_data[key] !== undefined) {
                  this.form_data[key] = this.normalize(key, answer);
                }
                // Check for the hasExtraIncome field
                if (key === "hasExtraIncome") {
                  this.has_extra_income = this.normalize(key, answer);
                }
              });
            } else if (typeof data === "object") {
              Object.entries(data).forEach(([key, answer]) => {
                if (this.form_data[key] !== undefined) {
                  this.form_data[key] = this.normalize(key, answer);
                }
                // Check for the hasExtraIncome field
                if (key === "hasExtraIncome") {
                  this.has_extra_income = this.normalize(key, answer);
                }
              });
            }
            
            // Auto-detect has_extra_income based on existing data
            if (this.has_extra_income === null && this.has_any_income_values()) {
              this.has_extra_income = true;
            }
          }
        },
        error: () => {
          Swal.fire({
            title: "Error",
            text: "Failed to fetch the answers, please try again later.",
            icon: "error",
            timer: 3000,
            timerProgressBar: true,
            showConfirmButton: false,
          });
        },
      });
    },
  },
  mounted() {
    this.fetch_sygnio_answers();
  },
};
